import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperienceDenormalizedV2,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { LinkIcon, MessageSquareText } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import ReactHelmet from 'react-helmet';
import { Spacer } from '../../../../../../components/Spacer';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useRouter } from '../../../../../../hooks/useRouter';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../layout/Flex';
import {
  useNumberQueryParam,
  useTypedStringQueryParam
} from '../../../../../../routes';
import { toFullName } from '../../../../../../services/user';
import { useTaskCompletionSuccessNotifications } from '../../../../../features/Experience/service';
import { useProductAnalytics } from '../../../../../features/Experience/services/productAnalytics';
import { useStandaloneAppThemeOverride } from '../../../../../hooks/useStandaloneAppContext';
import { WithBreakpoints } from '../../components/Breakpoints';
import { Contact } from '../../components/Contact';
import { Divider } from '../../components/Divider';
import { EmployeeAppPageLoader } from '../../components/EmployeeAppLoader';
import { InfoCard, InfoCardsContainer } from '../../components/InfoCards';
import { InstanceMainInboxNav } from '../../components/InstanceMainInboxNav';
import { InstanceMainWeekNav } from '../../components/InstanceMainWeekNav';
import { InboxNavItemKey, InboxStyleNav } from '../../components/Nav/InboxNav';
import {
  getStartingWeek,
  groupWeeks,
  WeekSelector
} from '../../components/Nav/WeekNav';
import {
  PageContainer,
  PageContainerInner,
  PageFrame,
  PageFrameHeader
} from '../../components/PageFrame';
import { ParticipantAwareTitle } from '../../components/ParticipantAwareTitle';
import { ProgressTracker } from '../../components/ProgressTracker';
import { Resource } from '../../components/Resource';
import { RoundIconContainer } from '../../components/RoundIconContainer';
import {
  getUrlForItem,
  SearchWithinInstanceButton
} from '../../components/Search/WithinInstance';
import { TopNavBarV2ForInstance } from '../../components/TopNav';
import { useEmployeeAppExperienceInstance } from '../../services/retrieval';
import { getIndividualInstanceUrl } from '../../services/routes';

const ContactsAndResources = ({
  instance,
  instanceAccessToken
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  instanceAccessToken: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { contactsV2, resources } = instance.data;
  const { trackEvent } = useProductAnalytics(instanceAccessToken);

  const subTitle =
    instance.data.actor !== PARTICIPANT_ACTOR_KEY
      ? t('for {{ name }}', { name: toFullName(instance.data.participant) })
      : undefined;

  const hasContactsOrResources = !!(contactsV2.length || resources.length);
  if (!hasContactsOrResources) {
    return null;
  }

  return (
    <InfoCardsContainer>
      {!!contactsV2.length && (
        <InfoCard
          title="Contacts"
          subTitle={subTitle}
          icon={
            <RoundIconContainer>
              <MessageSquareText size={24} />
            </RoundIconContainer>
          }
        >
          <FlexContainerVertical fullWidth spacing={2}>
            {instance.data.contactsV2.map((c, i) => (
              <React.Fragment key={i}>
                <Contact d={c} trackFn={trackEvent} />
                {i !== instance.data.contactsV2.length - 1 && (
                  <Divider margin={0} />
                )}
              </React.Fragment>
            ))}
          </FlexContainerVertical>
        </InfoCard>
      )}
      {!!resources.length && (
        <InfoCard
          title="Resources"
          subTitle={subTitle}
          icon={
            <RoundIconContainer>
              <LinkIcon size={24} />
            </RoundIconContainer>
          }
        >
          <FlexContainerVertical fullWidth spacing={1}>
            {instance.data.resources.map((r, i) => (
              <React.Fragment key={i}>
                <Resource d={r} trackFn={trackEvent} />
                {i !== instance.data.resources.length - 1 && (
                  <Divider margin={0} />
                )}
              </React.Fragment>
            ))}
          </FlexContainerVertical>
        </InfoCard>
      )}
    </InfoCardsContainer>
  );
};

const BodyHeader = ({
  instance
}: {
  instance: Doc<IExperienceDenormalizedV2>;
}) => {
  return (
    <PageFrameHeader>
      <ParticipantAwareTitle d={instance} />
      <ProgressTracker d={instance.data.progress} />
    </PageFrameHeader>
  );
};

type BodyProps = {
  instance: Doc<IExperienceDenormalizedV2>;
  actorAccessToken: string;
  instanceAccessToken: string;
};

const BodyWeekNavStyle = ({
  instance,
  actorAccessToken,
  instanceAccessToken
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const weekGroups = useMemo(() => groupWeeks(instance), [instance]);
  // based on where we are now.
  const n = useMemo(() => moment(), []);
  const currentWeek = useMemo(
    () => getStartingWeek(instance, weekGroups, n),
    []
  );
  const [selectedWeek] = useNumberQueryParam('w', currentWeek);

  const returnTo = getIndividualInstanceUrl(
    actorAccessToken,
    instanceAccessToken,
    { w: selectedWeek }
  );

  const { history } = useRouter();

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              allowGlobalSearch
              instance={instance}
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={<BodyHeader instance={instance} />}
          left={
            <WeekSelector
              instance={instance}
              weekGroups={weekGroups}
              currentWeek={currentWeek}
              selectedWeek={selectedWeek}
              setSelectedWeekToUrl={(w) =>
                getIndividualInstanceUrl(
                  actorAccessToken,
                  instanceAccessToken,
                  { w }
                )
              }
              trackFn={trackEvent}
              n={n}
            />
          }
          center={
            <>
              <InstanceMainWeekNav
                instance={instance}
                weekGroups={weekGroups}
                selectedWeek={selectedWeek}
                currentWeek={currentWeek}
                n={n}
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                returnTo={returnTo}
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={
            <ContactsAndResources
              instance={instance}
              instanceAccessToken={instanceAccessToken}
            />
          }
        />
      </PageContainerInner>
    </PageContainer>
  );
};

const BodyInboxStyle = ({
  instance,
  actorAccessToken,
  instanceAccessToken
}: BodyProps) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const n = useMemo(() => moment(), []);
  const [selectedGroup] = useTypedStringQueryParam<InboxNavItemKey>(
    'g',
    'everything'
  );
  const returnTo = getIndividualInstanceUrl(
    actorAccessToken,
    instanceAccessToken,
    { g: selectedGroup }
  );

  const { history } = useRouter();

  return (
    <PageContainer>
      <PageContainerInner>
        <TopNavBarV2ForInstance
          instance={instance}
          searchComponent={
            <SearchWithinInstanceButton
              instance={instance}
              allowGlobalSearch
              onSelectMatch={(item) => {
                const url = getUrlForItem(
                  item,
                  actorAccessToken,
                  instanceAccessToken,
                  returnTo
                );
                if (url) {
                  history.push(url);
                }
              }}
              closeOnSelect
            />
          }
        />
        <PageFrame
          aiDisclosure={instance.data.aiDisclosure}
          banner={instance.data.theming.banner}
          header={<BodyHeader instance={instance} />}
          left={
            <InboxStyleNav
              actorAccessToken={actorAccessToken}
              instanceAccessToken={instanceAccessToken}
              instance={instance}
              g={selectedGroup}
              trackFn={trackEvent}
            />
          }
          center={
            <>
              <InstanceMainInboxNav
                actorAccessToken={actorAccessToken}
                instanceAccessToken={instanceAccessToken}
                instance={instance}
                g={selectedGroup}
                returnTo={returnTo}
              />
              <WithBreakpoints lg={<Spacer height={8} />} />
            </>
          }
          right={
            <ContactsAndResources
              instance={instance}
              instanceAccessToken={instanceAccessToken}
            />
          }
        />
      </PageContainerInner>
    </PageContainer>
  );
};

export const EmployeeAppExperienceInstanceWithData = ({
  instance,
  actorAccessToken,
  instanceAccessToken
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  actorAccessToken: string;
  instanceAccessToken: string;
}) => {
  useStandaloneAppThemeOverride(instance?.data.org.experienceTheming || null);
  useTaskCompletionSuccessNotifications(instance);

  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const navStyle = instance.data.instancePageNavStyle;

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_INSTANCE,
      name: 'Single Instance Page Loaded',
      Explanation: 'A user opened the full view of a single instance',
      'User Role': 'Assignee',
      View: navStyle === 'INBOX' ? 'Status' : 'Timeline'
    });
  }, [trackEvent, navStyle]);

  return (
    <>
      <ReactHelmet>
        <title>{instance.data.title}</title>
      </ReactHelmet>
      {navStyle === 'INBOX' ? (
        <BodyInboxStyle
          instance={instance}
          actorAccessToken={actorAccessToken}
          instanceAccessToken={instanceAccessToken}
        />
      ) : (
        <BodyWeekNavStyle
          instance={instance}
          actorAccessToken={actorAccessToken}
          instanceAccessToken={instanceAccessToken}
        />
      )}
    </>
  );
};

export const EmployeeAppPageExperienceInstance = ({
  actorAccessToken,
  instanceAccessToken,
  v
}: {
  actorAccessToken: string;
  instanceAccessToken: string;
  v: number;
}) => {
  const [instance, loading, error] = useEmployeeAppExperienceInstance(
    actorAccessToken,
    instanceAccessToken,
    v
  );
  useErrorLogger('useEmployeeAppExperienceInstance', error);

  if (instance) {
    return (
      <EmployeeAppExperienceInstanceWithData
        instance={instance}
        actorAccessToken={actorAccessToken}
        instanceAccessToken={instanceAccessToken}
      />
    );
  }

  // TODO error case
  return <EmployeeAppPageLoader />;
};
